import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DashboardService } from '../../service/DashboardService';

const dashboardService = new DashboardService();

// Não existe "ano letivo" no domínio (Test.year/testYear é só uma tag manual
// do ano da prova, não um período calculado) — o default do heatmap é o ano
// calendário corrente (01/01 a 31/12), recalculado a cada chamada (não fixo
// no initialState, que só é avaliado uma vez no carregamento do módulo).
export const currentYearRange = () => {
    const year = new Date().getFullYear();
    return { from: `${year}-01-01`, to: `${year}-12-31` };
};

export const defaultStudentsRankingFilters = {
    moduleId: null, search: '', offset: 0, limit: 20, sortField: 'firstName', sortOrder: 1, activeOnly: true, startDate: null, endDate: null
};

const initialState = {
    moduleQuestions: {
        data: [],
        status: 'idle',
        error: null
    },
    residenceCombinations: {
        data: [],
        status: 'idle',
        error: null
    },
    medicalServicesUser: {
        data: [],
        status: 'idle',
        error: null
    },
    medicalServicesByState: {
        data: [],
        status: 'idle',
        error: null
    },
    testQuestions: {
        data: [],
        status: 'idle',
        error: null
    },
    importationsTimeline: {
        data: [],
        status: 'idle',
        error: null
    },
    dailyNewUsers: {
        data: [],
        status: 'idle',
        error: null
    },
    // Aproximação de usuários ativos agora (heartbeat do app, ver
    // getActiveUsersNow no backend) — não é presença em tempo real.
    activeUsersNow: {
        count: 0,
        status: 'idle',
        error: null
    },
    // Histórico persistido (reconstruído do AppSession, ver
    // getActiveUsersHourly) — diferente de activeUsersNow, que é só o
    // instante atual via polling.
    activeUsersHourly: {
        data: [],
        status: 'idle',
        error: null
    },
    // Histórico diário (não só um snapshot de hoje) — reconstruído a partir
    // de Subscription.start_on/end_on, não de studentsRanking (que tem
    // paginação/filtros próprios da tela de Ranking, sem serventia aqui).
    activeStudentsDaily: {
        data: [],
        status: 'idle',
        error: null
    },
    churnRiskCount: {
        count: 0,
        status: 'idle',
        error: null
    },
    // Histórico diário de sessões — mesmo padrão de dailyNewUsers.
    sessionsDailySummary: {
        data: [],
        status: 'idle',
        error: null
    },
    // Histórico diário de assinaturas vencendo nos próximos N dias.
    subscriptionsExpiringSoonDaily: {
        data: [],
        status: 'idle',
        error: null
    },
    modulePerformance: {
        data: [],
        status: 'idle',
        error: null,
        filters: { serviceId: null, uf: null, activeOnly: true }
    },
    // Mesmos dados do modulePerformance, só que quebrados por semana — pro
    // heatmap módulo × período. `filters.userId` distingue o heatmap
    // agregado (tela de Desempenho, sem userId) do individual (tela de
    // detalhes do aluno, com userId) — mesmo endpoint serve os dois.
    modulePerformanceHeatmap: {
        data: [],
        status: 'idle',
        error: null,
        filters: { from: null, to: null, serviceId: null, uf: null, userId: null, moduleId: null, activeOnly: true }
    },
    testPerformance: {
        data: [],
        status: 'idle',
        error: null,
        filters: { serviceId: null, uf: null, activeOnly: true }
    },
    // Mesma ideia do modulePerformanceHeatmap, agrupado por prova.
    testPerformanceHeatmap: {
        data: [],
        status: 'idle',
        error: null,
        filters: { from: null, to: null, serviceId: null, uf: null, userId: null, testId: null, activeOnly: true }
    },
    questionPerformance: {
        data: [],
        status: 'idle',
        error: null,
        filters: { moduleId: null, testId: null, questionId: null, serviceId: null, uf: null, limit: 100, activeOnly: true }
    },
    studentsRanking: {
        data: [],
        totalRecords: 0,
        status: 'idle',
        error: null,
        filters: defaultStudentsRankingFilters
    }
};

// Thunks para cada tipo de dado
export const loadModuleQuestionsAsync = createAsyncThunk(
    'dashboard/loadModuleQuestions',
    async (_, { rejectWithValue }) => {
        try {
            return await dashboardService.getModuleQuestionsCount();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadResidenceCombinationsAsync = createAsyncThunk(
    'dashboard/loadResidenceCombinations',
    async (activeOnly, { rejectWithValue }) => {
        try {
            return await dashboardService.getResidenceCombinations(activeOnly);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadMedicalServicesUserAsync = createAsyncThunk(
    'dashboard/loadMedicalServicesUser',
    async ({ activeOnly, from, to } = {}, { rejectWithValue }) => {
        try {
            return await dashboardService.getMedicalServicesUserCount(activeOnly, from, to);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadActiveUsersNowAsync = createAsyncThunk(
    'dashboard/loadActiveUsersNow',
    async (minutesThreshold, { rejectWithValue }) => {
        try {
            const res = await dashboardService.getActiveUsersNow(minutesThreshold);
            return res.activeUserCount;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadActiveUsersHourlyAsync = createAsyncThunk(
    'dashboard/loadActiveUsersHourly',
    async (hours, { rejectWithValue }) => {
        try {
            return await dashboardService.getActiveUsersHourly(hours);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Histórico diário (start_on/end_on por dia) — não usa getStudentsRanking
// (que só dá o total ATUAL, sem série histórica) nem mexe no slice
// studentsRanking, que tem paginação/filtros próprios da tela de Ranking.
export const loadActiveStudentsDailyAsync = createAsyncThunk(
    'dashboard/loadActiveStudentsDaily',
    async (days, { rejectWithValue }) => {
        try {
            return await dashboardService.getSubscriptionsActiveCountDaily(days);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadChurnRiskCountAsync = createAsyncThunk(
    'dashboard/loadChurnRiskCount',
    async (inactiveDays, { rejectWithValue }) => {
        try {
            const res = await dashboardService.getChurnCandidates(inactiveDays);
            return Array.isArray(res) ? res.length : 0;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadSessionsDailySummaryAsync = createAsyncThunk(
    'dashboard/loadSessionsDailySummary',
    async (days, { rejectWithValue }) => {
        try {
            return await dashboardService.getSessionsDailySummary(days);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadSubscriptionsExpiringSoonDailyAsync = createAsyncThunk(
    'dashboard/loadSubscriptionsExpiringSoonDaily',
    async ({ days, aheadDays } = {}, { rejectWithValue }) => {
        try {
            return await dashboardService.getSubscriptionsExpiringSoonCountDaily(days, aheadDays);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadMedicalServicesByStateAsync = createAsyncThunk(
    'dashboard/loadMedicalServicesByState',
    async (_, { rejectWithValue }) => {
        try {
            return await dashboardService.getMedicalServicesByState();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadTestQuestionsAsync = createAsyncThunk(
    'dashboard/loadTestQuestions',
    async (_, { rejectWithValue }) => {
        try {
            return await dashboardService.getTestQuestionsCount();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadImportationsTimelineAsync = createAsyncThunk(
    'dashboard/loadImportationsTimeline',
    async (_, { rejectWithValue }) => {
        try {
            return await dashboardService.getImportationsTimeline();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadDailyNewUsersAsync = createAsyncThunk(
    'dashboard/loadDailyNewUsers',
    async (_, { rejectWithValue }) => {
        try {
            return await dashboardService.getDailyNewUsers();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadModulePerformanceAsync = createAsyncThunk(
    'dashboard/loadModulePerformance',
    async (filters, { rejectWithValue }) => {
        try {
            return await dashboardService.getPerformanceByModule(filters);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadModulePerformanceHeatmapAsync = createAsyncThunk(
    'dashboard/loadModulePerformanceHeatmap',
    async (filters, { rejectWithValue }) => {
        try {
            return await dashboardService.getModulePerformanceHeatmap(filters);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadTestPerformanceAsync = createAsyncThunk(
    'dashboard/loadTestPerformance',
    async (filters, { rejectWithValue }) => {
        try {
            return await dashboardService.getPerformanceByTest(filters);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadTestPerformanceHeatmapAsync = createAsyncThunk(
    'dashboard/loadTestPerformanceHeatmap',
    async (filters, { rejectWithValue }) => {
        try {
            return await dashboardService.getTestPerformanceHeatmap(filters);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadQuestionPerformanceAsync = createAsyncThunk(
    'dashboard/loadQuestionPerformance',
    async (filters, { rejectWithValue }) => {
        try {
            return await dashboardService.getPerformanceByQuestion(filters);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadStudentsRankingAsync = createAsyncThunk(
    'dashboard/loadStudentsRanking',
    async (filters, { rejectWithValue }) => {
        try {
            return await dashboardService.getStudentsRanking(filters);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        resetDashboard: (state) => {
            Object.assign(state, initialState);
        },
        // Escopado só pro ranking — resetDashboard zera o slice inteiro
        // (compartilhado com Dashboard/Performance/Heatmap/etc.), o que
        // descartaria estado de outras páginas só porque a tabela de
        // ranking foi remontada.
        resetStudentsRanking: (state) => {
            state.studentsRanking = initialState.studentsRanking;
        }
    },
    extraReducers: (builder) => {
        // Module Questions
        builder
            .addCase(loadModuleQuestionsAsync.pending, (state) => {
                state.moduleQuestions.status = 'loading';
                state.moduleQuestions.error = null;
            })
            .addCase(loadModuleQuestionsAsync.fulfilled, (state, action) => {
                state.moduleQuestions.status = 'succeeded';
                state.moduleQuestions.data = action.payload;
                state.moduleQuestions.error = null;
            })
            .addCase(loadModuleQuestionsAsync.rejected, (state, action) => {
                state.moduleQuestions.status = 'failed';
                state.moduleQuestions.error = action.payload;
            })

            // Residence Combinations
            .addCase(loadResidenceCombinationsAsync.pending, (state) => {
                state.residenceCombinations.status = 'loading';
                state.residenceCombinations.error = null;
            })
            .addCase(loadResidenceCombinationsAsync.fulfilled, (state, action) => {
                state.residenceCombinations.status = 'succeeded';
                state.residenceCombinations.data = action.payload;
                state.residenceCombinations.error = null;
            })
            .addCase(loadResidenceCombinationsAsync.rejected, (state, action) => {
                state.residenceCombinations.status = 'failed';
                state.residenceCombinations.error = action.payload;
            })

            // Medical Services User
            .addCase(loadMedicalServicesUserAsync.pending, (state) => {
                state.medicalServicesUser.status = 'loading';
                state.medicalServicesUser.error = null;
            })
            .addCase(loadMedicalServicesUserAsync.fulfilled, (state, action) => {
                state.medicalServicesUser.status = 'succeeded';
                state.medicalServicesUser.data = action.payload;
                state.medicalServicesUser.error = null;
            })
            .addCase(loadMedicalServicesUserAsync.rejected, (state, action) => {
                state.medicalServicesUser.status = 'failed';
                state.medicalServicesUser.error = action.payload;
            })

            // Active Users Now — não seta status 'loading' no pending (só no
            // fulfilled/rejected): esse dado é atualizado por polling
            // periódico (ver ActiveUsersCard.js), então mostrar um spinner a
            // cada re-busca piscaria o card sem necessidade — mantém o
            // último valor visível até o próximo número chegar.
            .addCase(loadActiveUsersNowAsync.pending, (state) => {
                state.activeUsersNow.error = null;
            })
            .addCase(loadActiveUsersNowAsync.fulfilled, (state, action) => {
                state.activeUsersNow.status = 'succeeded';
                state.activeUsersNow.count = action.payload;
                state.activeUsersNow.error = null;
            })
            .addCase(loadActiveUsersNowAsync.rejected, (state, action) => {
                state.activeUsersNow.status = 'failed';
                state.activeUsersNow.error = action.payload;
            })

            // Active Users Hourly
            .addCase(loadActiveUsersHourlyAsync.pending, (state) => {
                state.activeUsersHourly.status = 'loading';
                state.activeUsersHourly.error = null;
            })
            .addCase(loadActiveUsersHourlyAsync.fulfilled, (state, action) => {
                state.activeUsersHourly.status = 'succeeded';
                state.activeUsersHourly.data = action.payload;
                state.activeUsersHourly.error = null;
            })
            .addCase(loadActiveUsersHourlyAsync.rejected, (state, action) => {
                state.activeUsersHourly.status = 'failed';
                state.activeUsersHourly.error = action.payload;
            })

            // Active Students Daily
            .addCase(loadActiveStudentsDailyAsync.pending, (state) => {
                state.activeStudentsDaily.status = 'loading';
                state.activeStudentsDaily.error = null;
            })
            .addCase(loadActiveStudentsDailyAsync.fulfilled, (state, action) => {
                state.activeStudentsDaily.status = 'succeeded';
                state.activeStudentsDaily.data = action.payload;
                state.activeStudentsDaily.error = null;
            })
            .addCase(loadActiveStudentsDailyAsync.rejected, (state, action) => {
                state.activeStudentsDaily.status = 'failed';
                state.activeStudentsDaily.error = action.payload;
            })

            // Churn Risk Count
            .addCase(loadChurnRiskCountAsync.pending, (state) => {
                state.churnRiskCount.status = 'loading';
                state.churnRiskCount.error = null;
            })
            .addCase(loadChurnRiskCountAsync.fulfilled, (state, action) => {
                state.churnRiskCount.status = 'succeeded';
                state.churnRiskCount.count = action.payload;
                state.churnRiskCount.error = null;
            })
            .addCase(loadChurnRiskCountAsync.rejected, (state, action) => {
                state.churnRiskCount.status = 'failed';
                state.churnRiskCount.error = action.payload;
            })

            // Sessions Daily Summary
            .addCase(loadSessionsDailySummaryAsync.pending, (state) => {
                state.sessionsDailySummary.status = 'loading';
                state.sessionsDailySummary.error = null;
            })
            .addCase(loadSessionsDailySummaryAsync.fulfilled, (state, action) => {
                state.sessionsDailySummary.status = 'succeeded';
                state.sessionsDailySummary.data = action.payload;
                state.sessionsDailySummary.error = null;
            })
            .addCase(loadSessionsDailySummaryAsync.rejected, (state, action) => {
                state.sessionsDailySummary.status = 'failed';
                state.sessionsDailySummary.error = action.payload;
            })

            // Subscriptions Expiring Soon Daily
            .addCase(loadSubscriptionsExpiringSoonDailyAsync.pending, (state) => {
                state.subscriptionsExpiringSoonDaily.status = 'loading';
                state.subscriptionsExpiringSoonDaily.error = null;
            })
            .addCase(loadSubscriptionsExpiringSoonDailyAsync.fulfilled, (state, action) => {
                state.subscriptionsExpiringSoonDaily.status = 'succeeded';
                state.subscriptionsExpiringSoonDaily.data = action.payload;
                state.subscriptionsExpiringSoonDaily.error = null;
            })
            .addCase(loadSubscriptionsExpiringSoonDailyAsync.rejected, (state, action) => {
                state.subscriptionsExpiringSoonDaily.status = 'failed';
                state.subscriptionsExpiringSoonDaily.error = action.payload;
            })

            // Medical Services By State
            .addCase(loadMedicalServicesByStateAsync.pending, (state) => {
                state.medicalServicesByState.status = 'loading';
                state.medicalServicesByState.error = null;
            })
            .addCase(loadMedicalServicesByStateAsync.fulfilled, (state, action) => {
                state.medicalServicesByState.status = 'succeeded';
                state.medicalServicesByState.data = action.payload;
                state.medicalServicesByState.error = null;
            })
            .addCase(loadMedicalServicesByStateAsync.rejected, (state, action) => {
                state.medicalServicesByState.status = 'failed';
                state.medicalServicesByState.error = action.payload;
            })

            // Test Questions
            .addCase(loadTestQuestionsAsync.pending, (state) => {
                state.testQuestions.status = 'loading';
                state.testQuestions.error = null;
            })
            .addCase(loadTestQuestionsAsync.fulfilled, (state, action) => {
                state.testQuestions.status = 'succeeded';
                state.testQuestions.data = action.payload;
                state.testQuestions.error = null;
            })
            .addCase(loadTestQuestionsAsync.rejected, (state, action) => {
                state.testQuestions.status = 'failed';
                state.testQuestions.error = action.payload;
            })

            // Importations Timeline
            .addCase(loadImportationsTimelineAsync.pending, (state) => {
                state.importationsTimeline.status = 'loading';
                state.importationsTimeline.error = null;
            })
            .addCase(loadImportationsTimelineAsync.fulfilled, (state, action) => {
                state.importationsTimeline.status = 'succeeded';
                state.importationsTimeline.data = action.payload;
                state.importationsTimeline.error = null;
            })
            .addCase(loadImportationsTimelineAsync.rejected, (state, action) => {
                state.importationsTimeline.status = 'failed';
                state.importationsTimeline.error = action.payload;
            })

            // Daily New Users
            .addCase(loadDailyNewUsersAsync.pending, (state) => {
                state.dailyNewUsers.status = 'loading';
                state.dailyNewUsers.error = null;
            })
            .addCase(loadDailyNewUsersAsync.fulfilled, (state, action) => {
                state.dailyNewUsers.status = 'succeeded';
                state.dailyNewUsers.data = action.payload;
                state.dailyNewUsers.error = null;
            })
            .addCase(loadDailyNewUsersAsync.rejected, (state, action) => {
                state.dailyNewUsers.status = 'failed';
                state.dailyNewUsers.error = action.payload;
            })

            // Module Performance
            .addCase(loadModulePerformanceAsync.pending, (state, action) => {
                state.modulePerformance.status = 'loading';
                state.modulePerformance.error = null;
                state.modulePerformance.filters = action.meta.arg || { serviceId: null, uf: null, activeOnly: true };
            })
            .addCase(loadModulePerformanceAsync.fulfilled, (state, action) => {
                state.modulePerformance.status = 'succeeded';
                state.modulePerformance.data = action.payload;
                state.modulePerformance.error = null;
            })
            .addCase(loadModulePerformanceAsync.rejected, (state, action) => {
                state.modulePerformance.status = 'failed';
                state.modulePerformance.error = action.payload;
            })

            // Module Performance Heatmap
            .addCase(loadModulePerformanceHeatmapAsync.pending, (state, action) => {
                state.modulePerformanceHeatmap.status = 'loading';
                state.modulePerformanceHeatmap.error = null;
                state.modulePerformanceHeatmap.filters = action.meta.arg || { from: null, to: null, serviceId: null, uf: null, userId: null, moduleId: null, activeOnly: true };
            })
            .addCase(loadModulePerformanceHeatmapAsync.fulfilled, (state, action) => {
                state.modulePerformanceHeatmap.status = 'succeeded';
                state.modulePerformanceHeatmap.data = action.payload;
                state.modulePerformanceHeatmap.error = null;
            })
            .addCase(loadModulePerformanceHeatmapAsync.rejected, (state, action) => {
                state.modulePerformanceHeatmap.status = 'failed';
                state.modulePerformanceHeatmap.error = action.payload;
            })

            // Test Performance
            .addCase(loadTestPerformanceAsync.pending, (state, action) => {
                state.testPerformance.status = 'loading';
                state.testPerformance.error = null;
                state.testPerformance.filters = action.meta.arg || { serviceId: null, uf: null, activeOnly: true };
            })
            .addCase(loadTestPerformanceAsync.fulfilled, (state, action) => {
                state.testPerformance.status = 'succeeded';
                state.testPerformance.data = action.payload;
                state.testPerformance.error = null;
            })
            .addCase(loadTestPerformanceAsync.rejected, (state, action) => {
                state.testPerformance.status = 'failed';
                state.testPerformance.error = action.payload;
            })

            // Test Performance Heatmap
            .addCase(loadTestPerformanceHeatmapAsync.pending, (state, action) => {
                state.testPerformanceHeatmap.status = 'loading';
                state.testPerformanceHeatmap.error = null;
                state.testPerformanceHeatmap.filters = action.meta.arg || { from: null, to: null, serviceId: null, uf: null, userId: null, testId: null, activeOnly: true };
            })
            .addCase(loadTestPerformanceHeatmapAsync.fulfilled, (state, action) => {
                state.testPerformanceHeatmap.status = 'succeeded';
                state.testPerformanceHeatmap.data = action.payload;
                state.testPerformanceHeatmap.error = null;
            })
            .addCase(loadTestPerformanceHeatmapAsync.rejected, (state, action) => {
                state.testPerformanceHeatmap.status = 'failed';
                state.testPerformanceHeatmap.error = action.payload;
            })

            // Question Performance
            .addCase(loadQuestionPerformanceAsync.pending, (state, action) => {
                state.questionPerformance.status = 'loading';
                state.questionPerformance.error = null;
                state.questionPerformance.filters = action.meta.arg || { moduleId: null, testId: null, questionId: null, serviceId: null, uf: null, limit: 100, activeOnly: true };
            })
            .addCase(loadQuestionPerformanceAsync.fulfilled, (state, action) => {
                state.questionPerformance.status = 'succeeded';
                state.questionPerformance.data = action.payload;
                state.questionPerformance.error = null;
            })
            .addCase(loadQuestionPerformanceAsync.rejected, (state, action) => {
                state.questionPerformance.status = 'failed';
                state.questionPerformance.error = action.payload;
            })

            // Students Ranking
            .addCase(loadStudentsRankingAsync.pending, (state, action) => {
                state.studentsRanking.status = 'loading';
                state.studentsRanking.error = null;
                state.studentsRanking.filters = action.meta.arg
                    || { moduleId: null, search: '', offset: 0, limit: 20, sortField: 'firstName', sortOrder: 1, activeOnly: true, startDate: null, endDate: null };
            })
            .addCase(loadStudentsRankingAsync.fulfilled, (state, action) => {
                state.studentsRanking.status = 'succeeded';
                state.studentsRanking.data = action.payload.data;
                state.studentsRanking.totalRecords = action.payload.totalRecords;
                state.studentsRanking.error = null;
            })
            .addCase(loadStudentsRankingAsync.rejected, (state, action) => {
                state.studentsRanking.status = 'failed';
                state.studentsRanking.error = action.payload;
            });
    }
});

export const { resetDashboard, resetStudentsRanking } = dashboardSlice.actions;

// Selectors
export const selectModuleQuestions = (state) => state.dashboard.moduleQuestions;
export const selectResidenceCombinations = (state) => state.dashboard.residenceCombinations;
export const selectMedicalServicesUser = (state) => state.dashboard.medicalServicesUser;
export const selectMedicalServicesByState = (state) => state.dashboard.medicalServicesByState;
export const selectTestQuestions = (state) => state.dashboard.testQuestions;
export const selectImportationsTimeline = (state) => state.dashboard.importationsTimeline;
export const selectDailyNewUsers = (state) => state.dashboard.dailyNewUsers;
export const selectActiveUsersNow = (state) => state.dashboard.activeUsersNow;
export const selectActiveUsersHourly = (state) => state.dashboard.activeUsersHourly;
export const selectActiveStudentsDaily = (state) => state.dashboard.activeStudentsDaily;
export const selectChurnRiskCount = (state) => state.dashboard.churnRiskCount;
export const selectSessionsDailySummary = (state) => state.dashboard.sessionsDailySummary;
export const selectSubscriptionsExpiringSoonDaily = (state) => state.dashboard.subscriptionsExpiringSoonDaily;
export const selectModulePerformance = (state) => state.dashboard.modulePerformance;
export const selectModulePerformanceHeatmap = (state) => state.dashboard.modulePerformanceHeatmap;
export const selectTestPerformance = (state) => state.dashboard.testPerformance;
export const selectTestPerformanceHeatmap = (state) => state.dashboard.testPerformanceHeatmap;
export const selectQuestionPerformance = (state) => state.dashboard.questionPerformance;
export const selectStudentsRanking = (state) => state.dashboard.studentsRanking;

export default dashboardSlice.reducer; 